/**
 * KNOWN GAP (tracked in DEV-839): KpiGridTable rows currently do NOT
 * carry `hse_bundle`. The helper is wired into `getKpiIndicator` and
 * degrades cleanly (returns engine color unchanged) when the bundle is
 * absent — so there is no behavior change today, but the floor cap also
 * does not apply on the grid.
 *
 * The fix requires a bulk-incidents endpoint (or a server-side
 * pre-aggregated `worst_hse_color` column) so App.tsx can attach
 * hse_bundle to every row without N round trips. Once that ships, the
 * grid lights up automatically — no further changes to consumers.
 *
 * GENERALIZATION (DEV-840 reserved): The helper returns an `adjustments[]`
 * list — a tagged union of all reasons the displayed color differs from
 * raw engine output. Today there is exactly one variant: HSE floor cap.
 * Future variants will follow the same shape:
 *
 *   1. Viewer trust overlay (advisor's ask). PER-USER private annotation
 *      that adjusts the color a particular Govern user sees for a
 *      project/region. Computed by the helper itself from a future
 *      `viewerContext` parameter (the slot is reserved on the function
 *      signatures below). NEVER mutates the row; never visible to other
 *      viewers; never affects the engine. See the architectural rules
 *      in the discussion thread on the trust overlay.
 *
 *   2. Compliance / regulatory hold (hypothetical). System-wide adjustment
 *      driven by external state (e.g. a compliance officer flags a
 *      project pending audit). Same shape as HSE: attached to the row in
 *      App.tsx, read by the helper.
 *
 * Adjustments compose in order. Worse-of semantics for color (red beats
 * yellow beats green). Each adjustment carries enough metadata for the
 * renderer to localize and explain it. The renderer iterates the list,
 * so adding a third variant requires no changes to call sites.
 *
 * Display KPI state helpers (DEV-822 / DEV-838).
 *
 * THE PROBLEM
 * -----------
 * The platform displays Mission and Summary KPI colors in MULTIPLE places:
 *
 *   1. Project-detail KpiSummaryHeader (the hero row at top of ProjectDetail)
 *   2. KpiGridTable (cross-project list view)
 *   3. PerformanceSummary cards
 *   4. MissionStatusCard (compact row on the Summary tab)
 *
 * Each of those surfaces independently reads `mission_indicator_color` and
 * `summary_indicator_color` off the project row. When the HSE floor cap was
 * added (DEV-822 § 10), the rule "Mission/Summary color = worse of (engine
 * color, worst HSE color)" was implemented INSIDE KpiSummaryHeader only.
 *
 * Result: the project-detail hero row shows yellow (capped), but the same
 * project in KpiGridTable still shows green. Stakeholders see two different
 * answers for the same question. That's a credibility-breaking drift.
 *
 * THE FIX (this file)
 * -------------------
 * One helper. Everyone calls it. The floor-cap rule lives here, and only
 * here. New surfaces that show Mission/Summary colors MUST go through this
 * module — never read `mission_indicator_color` directly.
 *
 * THE INVARIANT
 * -------------
 * For any project, on any surface:
 *
 *   getMissionDisplayState(item).color === getMissionDisplayState(item).color
 *
 * This is enforced by the cross-surface parity test in
 * `src/Data/__tests__/displayKpiState.parity.test.ts`. If you find yourself
 * computing color anywhere outside this file, stop and use the helper.
 *
 * THE SCORE NUMBER IS NEVER CAPPED
 * --------------------------------
 * Floor cap affects color only. Mission score 84 stays 84 even when the
 * pill is yellow because of an HSE recordable. This is intentional per spec
 * — we want stakeholders to see honest signal (color = "watch this") without
 * mathematically penalizing the underlying performance number.
 */

import { applyHseFloorCap, type HseBundle, type HseColor } from '../kpi-engine/hse';

/* ------------------------------------------------------------------------- */
/*  Color code mapping                                                        */
/* ------------------------------------------------------------------------- */

/**
 * The legacy indicator color code stored on `mission_indicator_color`,
 * `summary_indicator_color`, etc.
 *
 *   1 = red
 *   2 = yellow
 *   3 = green
 *   0 / null / undefined = grey (no data)
 *
 * This is the wire format. HseColor is the in-engine format. We translate
 * at the boundary so the rest of the app keeps using whichever it already
 * uses.
 */
export type IndicatorColorCode = 0 | 1 | 2 | 3 | null;

function safeNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Convert wire-format color code (1/2/3) → engine-format HseColor.
 * Returns null for grey/no-data so callers can decide how to render that.
 */
export function colorCodeToHseColor(code: unknown): HseColor | null {
  const n = safeNum(code);
  if (n === null || n === 0) return null;
  if (n === 1) return 'red';
  if (n === 2) return 'yellow';
  if (n === 3) return 'green';
  // Fractional/legacy values — bucket the same way KpiSummaryHeader does.
  if (n <= 0.5) return null;
  if (n <= 1.5) return 'red';
  if (n <= 2.5) return 'yellow';
  return 'green';
}

/** Convert engine HseColor → wire-format integer color code. */
export function hseColorToColorCode(c: HseColor): IndicatorColorCode {
  if (c === 'red') return 1;
  if (c === 'yellow') return 2;
  return 3;
}

/* ------------------------------------------------------------------------- */
/*  Public types                                                              */
/* ------------------------------------------------------------------------- */

/**
 * One reason the displayed color differs from the raw engine color.
 *
 * Tagged union — `source` is the discriminator. Renderers switch on it to
 * produce a localized explanation. Adding a new variant is a code change
 * to this file plus the renderer; no call sites change.
 *
 * Today: exactly one variant (`hse_floor_cap`).
 * Reserved future variants (see file-level GENERALIZATION note):
 *   - `viewer_trust_overlay` — per-viewer private adjustment
 *   - `compliance_hold` — system-wide regulatory flag
 */
export type KpiAdjustment =
  | {
      source: 'hse_floor_cap';
      /** Which HSE category drove the cap. */
      cappedByCategory: 'health' | 'safety' | 'environment';
    };
  // | { source: 'viewer_trust_overlay'; reason: string; appliedBy: string }
  // | { source: 'compliance_hold'; reason: string; appliedAt: string }

/**
 * The unified display state for one KPI cell (Mission or Summary).
 * Every surface MUST render from this — never from raw item fields.
 */
export interface DisplayKpiState {
  /**
   * Final color to display, AFTER all adjustments are applied.
   * Wire format (1/2/3/0) so existing renderers can consume it directly.
   */
  color: IndicatorColorCode;
  /**
   * Original engine color BEFORE any adjustments. Useful for tooltips
   * and debug surfaces that want to explain why the color changed.
   */
  originalColor: IndicatorColorCode;
  /**
   * The score number (0-100). NEVER affected by adjustments — only color
   * is overridden, per spec. Null when there's no scoreable data.
   */
  score: number | null;
  /**
   * Trend value (-1, 0, 1, null). Pass-through; adjustments don't change
   * trend.
   */
  trend: number | null;
  /**
   * Ordered list of adjustments that changed the color. Empty when the
   * displayed color matches the engine color exactly. Renderers iterate
   * this to surface explanations (corner badge + tooltip / popover).
   *
   * Today: 0 or 1 entries (HSE only). When viewer trust overlay ships,
   * a single cell may carry 2+ entries — the renderer must handle that.
   */
  adjustments: KpiAdjustment[];
  /**
   * DEV-851: Reliability-only flag. When true, the project is Completed
   * (status=Completed AND end_date in the past). Renderers MUST display
   * the blue Complete state instead of the color-band score.
   *
   * Always false for Mission and Summary — those don't have a Complete
   * state (Mission KPIs continue tracking against historical baselines
   * even after project closure).
   *
   * Per CLAUDE.md color semantics: blue = Complete / Done. Forcing
   * green on completed projects would conflate "done" with "on track."
   */
  isComplete?: boolean;
}

/**
 * Minimum shape required from a project row for display computation.
 * Keep narrow — don't pull in the full project type, callers vary.
 */
export interface DisplayableKpiItem {
  mission_indicator_color?: unknown;
  mission_indicator_trend?: unknown;
  mission_score_0_100?: unknown;
  summary_indicator_color?: unknown;
  summary_indicator_trend?: unknown;
  summary_score_0_100?: unknown;
  /**
   * DEV-851: Data reliability fields needed by getDataReliabilityDisplayState.
   * Optional because Mission/Summary helpers don't read them.
   */
  data_reliability_color?: unknown;
  data_reliability_trend?: unknown;
  data_reliability_score_0_100?: unknown;
  /**
   * DEV-851: Set by App.tsx when project is Completed (status=Completed AND
   * end_date in the past). Drives the blue Complete-state override on the
   * five at-a-glance reliability surfaces.
   */
  _isCompletedProject?: unknown;
  /**
   * Pre-computed HSE bundle for this project. When present, the floor cap
   * is applied. When absent (or worst_color === 'green'), no cap — display
   * is identical to engine output. This field is added in App.tsx during
   * the same memo that builds the rest of the row.
   */
  hse_bundle?: HseBundle | null;
}

/* ------------------------------------------------------------------------- */
/*  Internal: adjustment pipeline                                             */
/*                                                                            */
/*  Each adjustment is a pure step that takes the current color and an        */
/*  input, and returns either no change or a new color + KpiAdjustment.       */
/*  applyAdjustments composes them in order. To add a new adjustment          */
/*  (e.g. viewer trust overlay), write a new step function and append it      */
/*  to the pipeline below.                                                    */
/* ------------------------------------------------------------------------- */

interface PipelineResult {
  finalCode: IndicatorColorCode;
  originalCode: IndicatorColorCode;
  adjustments: KpiAdjustment[];
}

function normalizeColorCode(rawCode: unknown): IndicatorColorCode {
  const n = safeNum(rawCode);
  if (n === null) return null;
  if (n === 0) return 0;
  if (n === 1) return 1;
  if (n === 2) return 2;
  if (n === 3) return 3;
  return null;
}

/**
 * HSE floor-cap adjustment step. Pure function. Returns null when no
 * adjustment is needed, otherwise returns the new color + the attribution.
 */
function hseFloorCapStep(
  currentColor: IndicatorColorCode,
  hse: HseBundle | null | undefined,
): { color: IndicatorColorCode; adjustment: KpiAdjustment } | null {
  if (!hse || hse.worst_color === 'green' || !hse.capped_by_category) {
    return null;
  }
  // For grey/null current color, treat as green-or-better so HSE alert
  // wins outright (otherwise grey would silently suppress active alerts).
  const engineColor: HseColor =
    currentColor === null || currentColor === 0
      ? 'green'
      : (colorCodeToHseColor(currentColor) ?? 'green');
  const cappedColor = applyHseFloorCap(engineColor, hse);
  const newCode = hseColorToColorCode(cappedColor);
  if (newCode === currentColor) return null;
  return {
    color: newCode,
    adjustment: {
      source: 'hse_floor_cap',
      cappedByCategory: hse.capped_by_category,
    },
  };
}

function applyAdjustments(
  rawCode: unknown,
  item: DisplayableKpiItem,
  // RESERVED for DEV-840 (viewer trust overlay). Adding the parameter now
  // so call sites that adopt it don't need a signature change later.
  // _viewerContext?: ViewerContext,
): PipelineResult {
  const originalCode = normalizeColorCode(rawCode);
  let currentCode = originalCode;
  const adjustments: KpiAdjustment[] = [];

  // Step 1: HSE floor cap (system-wide).
  const hseStep = hseFloorCapStep(currentCode, item.hse_bundle ?? null);
  if (hseStep) {
    currentCode = hseStep.color;
    adjustments.push(hseStep.adjustment);
  }

  // Step 2 (FUTURE): viewer trust overlay. Will be appended here when
  //   DEV-840 ships:
  //
  //     const trustStep = viewerTrustStep(currentCode, item, _viewerContext);
  //     if (trustStep) { currentCode = trustStep.color; adjustments.push(trustStep.adjustment); }
  //
  // Worse-of semantics compose naturally because each step takes the
  // current (post-prior-adjustments) color as input.

  return { finalCode: currentCode, originalCode, adjustments };
}

/* ------------------------------------------------------------------------- */
/*  Public API                                                                */
/*                                                                            */
/*  CONTRACT: Every surface that renders Mission/Summary color MUST go        */
/*  through these helpers. Reading `mission_indicator_color` / etc. directly  */
/*  is a regression — the parity test in __tests__/displayKpiState.test.ts    */
/*  exists to catch new code that bypasses this module.                       */
/*                                                                            */
/*  RESERVED FUTURE PARAMETER: Both helpers will accept an optional second    */
/*  `viewerContext` argument for the per-viewer trust overlay (DEV-840).      */
/*  The contract requires viewerContext to carry only viewer-private          */
/*  annotations (project- or region-level, never PM-level), and the result    */
/*  must remain a pure function of (item, viewerContext) so memoization       */
/*  works. Adding the parameter is a no-op until trust overlay ships.         */
/* ------------------------------------------------------------------------- */

/**
 * Compute the final Mission display state for a project row, applying any
 * adjustments (today: HSE floor cap; future: viewer trust overlay).
 */
export function getMissionDisplayState(
  item: DisplayableKpiItem | null | undefined,
): DisplayKpiState {
  if (!item) return EMPTY_STATE;
  const result = applyAdjustments(item.mission_indicator_color, item);
  return {
    color: result.finalCode,
    originalColor: result.originalCode,
    score: safeNum(item.mission_score_0_100),
    trend: safeNum(item.mission_indicator_trend),
    adjustments: result.adjustments,
  };
}

/**
 * Compute the final Summary display state for a project row. Summary inherits
 * the Mission floor cap per spec § 10 (since Mission is one of its inputs)
 * — but only the COLOR is adjusted; the engine-computed Summary score number
 * is preserved.
 */
export function getSummaryDisplayState(
  item: DisplayableKpiItem | null | undefined,
): DisplayKpiState {
  if (!item) return EMPTY_STATE;
  const result = applyAdjustments(item.summary_indicator_color, item);
  return {
    color: result.finalCode,
    originalColor: result.originalCode,
    score: safeNum(item.summary_score_0_100),
    trend: safeNum(item.summary_indicator_trend),
    adjustments: result.adjustments,
  };
}

/**
 * DEV-851: Compute the final Data Reliability display state for a project row.
 *
 * Single point of truth for the blue Complete-state override. When
 * `_isCompletedProject` is true, `isComplete` is set on the result and
 * renderers MUST display the blue Complete pill/badge instead of the
 * normal color-band score. Otherwise renderers use color/score/trend
 * exactly as Mission/Summary do.
 *
 * The HSE floor cap does NOT apply to Data Reliability — reliability
 * measures whether reports are submitted on time, which is independent
 * of HSE incident state. Adjustments list is therefore always empty.
 *
 * CONTRACT: Every surface that renders the project's reliability score
 * MUST go through this helper. Reading `data_reliability_color` /
 * `data_reliability_score_0_100` directly bypasses the Complete-state
 * override and produces grey N/A on completed projects, which is wrong
 * per CLAUDE.md color semantics (blue = Complete).
 */
export function getDataReliabilityDisplayState(
  item: DisplayableKpiItem | null | undefined,
): DisplayKpiState {
  if (!item) return EMPTY_STATE;
  const originalCode = normalizeColorCode(item.data_reliability_color);
  return {
    color: originalCode,
    originalColor: originalCode,
    score: safeNum(item.data_reliability_score_0_100),
    trend: safeNum(item.data_reliability_trend),
    adjustments: [],
    isComplete: !!item._isCompletedProject,
  };
}

const EMPTY_STATE: DisplayKpiState = {
  color: null,
  originalColor: null,
  score: null,
  trend: null,
  adjustments: [],
};

/* ------------------------------------------------------------------------- */
/*  Convenience: compute both at once                                         */
/* ------------------------------------------------------------------------- */

export interface DisplayKpiStateBundle {
  mission: DisplayKpiState;
  summary: DisplayKpiState;
}

export function getDisplayKpiStates(
  item: DisplayableKpiItem | null | undefined,
): DisplayKpiStateBundle {
  return {
    mission: getMissionDisplayState(item),
    summary: getSummaryDisplayState(item),
  };
}

/* ------------------------------------------------------------------------- */
/*  Adjustment renderer helper                                                */
/* ------------------------------------------------------------------------- */

/**
 * Convenience for renderers: produce a localized one-line label for a
 * single adjustment. Returns null if the i18n function or the variant is
 * unrecognized.
 *
 * The renderer is responsible for joining multiple adjustments (e.g. with
 * a separator, or rendering each as a row in a popover). Keeping this
 * helper one-at-a-time makes it trivial to render either way.
 */
export function describeAdjustment(
  adjustment: KpiAdjustment,
  t: (key: string, params?: Record<string, string>) => string,
): string | null {
  switch (adjustment.source) {
    case 'hse_floor_cap': {
      const categoryLabel = t(`hse.category.${adjustment.cappedByCategory}`);
      // Falls back to a sensible English string if the i18n key is missing.
      const localized = t('summary.adjustment.hseFloorCap', { category: categoryLabel });
      return localized && localized !== 'summary.adjustment.hseFloorCap'
        ? localized
        : `Color reflects an active ${categoryLabel} HSE alert — score number unchanged`;
    }
    default:
      return null;
  }
}
